/**
 * PlanFeatureValue (PFV) helpers.
 *
 * Canonical in-memory shape (one place, one source of truth):
 *
 *   {
 *     _id,                // PlanFeatureValue cmscontent _id
 *     plan,               // ProductPlan _id (string)
 *     param,              // display label, localizable
 *     value,              // machine value
 *     params,             // optional object of extra key/value pairs
 *     localization        // doc-level localization array
 *   }
 *
 * Anything outside that shape is *not* a PFV field and must not be persisted.
 * This is the only allow-list — no block-lists anywhere.
 */

export const pfvLocKey = (pfvId) => `PlanFeatureValue-param-${pfvId}`

const refId = (v) => {
  if (!v) return null
  if (Array.isArray(v)) return refId(v[0])
  if (typeof v === "string") return v
  if (typeof v === "object") {
    if (v._id) return String(v._id)
    if (v.item) return String(v.item)
  }
  return null
}

/**
 * Build the canonical in-memory PFV from a raw cmscontent doc (`expand:false`).
 */
function toPfv(doc) {
  const c = doc?.content || {}
  return {
    _id: String(doc._id),
    plan: refId(c.plan) || "",
    param: c.param ?? "",
    value: c.value ?? "",
    params: c.params && typeof c.params === "object" ? { ...c.params } : {},
    localization: Array.isArray(doc.localization) ? doc.localization : [],
  }
}

/**
 * Fetch all PFV docs and return a map keyed by _id → canonical PFV shape.
 */
export async function loadPfvMap(getContent) {
  const res = await getContent({
    match: { "content.type": "PlanFeatureValue" },
    itemsPerPage: 5000,
    expand: false,
  })
  const map = new Map()
  ;(res?.data || []).forEach((d) => {
    map.set(String(d._id), toPfv(d))
  })
  return map
}

/**
 * Replace each feature.plan_values (array of ids) with hydrated PFV objects.
 */
export function hydrateFeatures(features, pfvMap) {
  return features.map((f) => {
    const raw = Array.isArray(f.plan_values) ? f.plan_values : []
    const resolved = raw
      .map((entry) => {
        const id = refId(entry)
        if (id && pfvMap.has(id)) return pfvMap.get(id)
        return null
      })
      .filter(Boolean)
    const rawCat = Array.isArray(f.category) ? f.category[0] : f.category
    const category = refId(rawCat) || rawCat
    return { ...f, plan_values: resolved, category }
  })
}

/**
 * Upsert a single PFV. Reads only the canonical fields off `pv` — anything
 * else on the object is silently dropped. Returns the persisted _id.
 */
export async function upsertPlanFeatureValue(saveContent, pv) {
  const _id = pv._id
  const planId = refId(pv.plan)
  const content = {
    type: "PlanFeatureValue",
    plan: planId,
    param: pv.param ?? "",
    value: pv.value ?? "",
    params: pv.params && typeof pv.params === "object" ? { ...pv.params } : {},
  }
  const payload = {
    ...(_id ? { _id } : {}),
    content,
    localization: _id ? [{ field: "param", localizationKey: pfvLocKey(_id) }] : [],
  }
  const res = await saveContent({ payload }, false)
  // Backend (CMS.saveData) returns { result: <_id> } on success.
  const persistedId = _id || res?.result || res?._id || res?.data?._id
  if (!persistedId || res?.error) {
    throw new Error(`PFV save failed: ${res?.error || "no _id returned"}`)
  }
  // New doc: now that the id exists, set the localization key referencing it.
  if (!_id) {
    await saveContent(
      {
        payload: {
          _id: persistedId,
          localization: [{ field: "param", localizationKey: pfvLocKey(persistedId) }],
        },
      },
      false,
    )
  }
  return String(persistedId)
}

export async function deletePlanFeatureValue(deleteContent, pfvId) {
  if (!pfvId) return
  await deleteContent(pfvId)
}

/**
 * Persist the full set of plan_values for a feature: upsert each PFV, then
 * delete any PFVs that were in the previous list but not the new one.
 */
export async function savePlanValues({ saveContent, deleteContent }, planValues, previousIds = []) {
  const persistedIds = []
  for (const pv of planValues || []) {
    const id = await upsertPlanFeatureValue(saveContent, pv)
    persistedIds.push(id)
  }
  const keep = new Set(persistedIds)
  for (const oldId of previousIds) {
    if (!keep.has(String(oldId))) {
      await deletePlanFeatureValue(deleteContent, oldId)
    }
  }
  return persistedIds
}
